import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Download, Pencil, Trash2, Loader2, Sparkles, Lock } from "lucide-react";
import { api, errMsg, download, streamAI } from "@/lib/api";
import { PageHeader, StatusBadge, Empty } from "@/components/Brand";
import { Field, SimpleSelect, useTeam, useClauses } from "@/components/FormField";
import { fmtDate } from "@/constants/options";
import { useAuth } from "@/context/AuthContext";

const PLAN_FEATURE = { audits: "capa_audit", capa: "capa_audit", reviews: "capa_audit", equipment: "resources", personnel: "resources", risks: "advanced_dash", indicators: "advanced_dash" };

export const Markdown = ({ children }) => <div className="qt-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown></div>;

export function AIBox({ body, label = "Ask AI", note = "AI suggestions are proposals and require human evaluation." }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setText("");
    try { await streamAI(body, setText); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#48217A]/20 bg-[#F3EFFA]/50 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-[#5A6B87]">{note}</span>
        <Button size="sm" type="button" onClick={run} disabled={busy} className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid="ai-box-run">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}{label}
        </Button>
      </div>
      {text && <div className="bg-white rounded-md p-3 max-h-80 overflow-y-auto" data-testid="ai-box-output"><Markdown>{text}</Markdown></div>}
    </div>
  );
}

const display = (f, v, team, clauses) => {
  if (v == null || v === "") return "—";
  if (f?.type === "user") return team.find((u) => u.id === v)?.name || "—";
  if (f?.type === "clause") return clauses.find((c) => c.id === v)?.clause_no || "—";
  if (f?.type === "date") return fmtDate(v);
  if (Array.isArray(v)) return `${v.length} item(s)`;
  return String(v);
};

export default function ModulePage({ cfg }) {
  const { user } = useAuth();
  const lockedFeat = PLAN_FEATURE[cfg.module];
  const locked = lockedFeat && user?.plan && !user.plan.features.includes(lockedFeat);
  const team = useTeam(), clauses = useClauses();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const fieldMap = useMemo(() => Object.fromEntries(cfg.fields.map((f) => [f.key, f])), [cfg]);
  const load = () => api.get(`/m/${cfg.module}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [cfg.module]);
  const shown = items.filter((i) => (!status || i.status === status) && (!q || JSON.stringify(i).toLowerCase().includes(q.toLowerCase())));
  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.patch(`/m/${cfg.module}/${edit.id}`, edit); else await api.post(`/m/${cfg.module}`, edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    try { await api.delete(`/m/${cfg.module}/${del.id}`); toast.success("Removed"); setDel(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow={cfg.eyebrow} title={cfg.title} desc={cfg.desc}>
        {cfg.report && <Button variant="outline" onClick={() => download(`/reports/${cfg.report}?format=xlsx`, `${cfg.report}.xlsx`)} data-testid={`${cfg.module}-export-btn`}><Download className="h-4 w-4 mr-1" />Export</Button>}
        <Button disabled={locked} onClick={() => setEdit({ ...(cfg.defaults || {}) })} className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid={`${cfg.module}-add-btn`}><Plus className="h-4 w-4 mr-1" />{cfg.addLabel || "Add"}</Button>
      </PageHeader>
      {locked && <div className="qt-card p-3 mb-4 text-sm bg-[#F3EFFA] text-[#48217A] flex flex-wrap gap-2 items-center" data-testid="module-locked-notice"><Lock className="h-4 w-4" />{user.plan.state === "expired" ? "Your plan has expired." : `This module is not included in the ${user.plan.plan_name} plan.`} Existing records stay viewable. <Link to="/billing" className="font-semibold underline">Upgrade</Link></div>}
      {cfg.banner}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative w-full sm:w-72"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9 bg-white" data-testid={`${cfg.module}-search`} /></div>
        {fieldMap.status && <div className="w-52"><SimpleSelect value={status} onChange={setStatus} options={fieldMap.status.options} placeholder="All statuses" allowNone testid={`${cfg.module}-status-filter`} /></div>}
      </div>
      {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#48217A]" /></div> : shown.length === 0 ? (
        <Empty icon={cfg.icon} title={`No ${cfg.title.toLowerCase()} yet`} desc={cfg.emptyHint}><Button onClick={() => setEdit({ ...(cfg.defaults || {}) })} className="bg-[#48217A]" data-testid={`${cfg.module}-empty-add`}>{cfg.addLabel || "Add"}</Button></Empty>
      ) : (
        <div className="qt-card overflow-x-auto">
          <table className="w-full text-[13px]" data-testid={`${cfg.module}-table`}>
            <thead className="bg-[#F4F6F8] text-[11px] uppercase tracking-wider text-[#5A6B87]">
              <tr><th className="text-left px-3 py-2.5">Ref</th>{cfg.columns.map((c) => <th key={c} className="text-left px-3 py-2.5 whitespace-nowrap">{c === "score" ? "Score" : c === "latest_value" ? "Latest %" : fieldMap[c]?.label || c}</th>)}<th /></tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.id} className="border-t hover:bg-[#F3EFFA]/40 cursor-pointer" onClick={() => setEdit({ ...i })} data-testid={`${cfg.module}-row-${i.ref}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-[#48217A]">{i.ref}</td>
                  {cfg.columns.map((c) => (
                    <td key={c} className="px-3 py-2.5 max-w-xs truncate">
                      {c === "status" ? <StatusBadge status={i.status} /> : cfg.cell?.(c, i) ?? display(fieldMap[c], i[c], team, clauses)}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEdit({ ...i })} className="p-1.5 text-slate-500 hover:text-[#48217A]" data-testid={`${cfg.module}-edit-${i.ref}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setDel(i)} className="p-1.5 text-slate-500 hover:text-[#D64545]" data-testid={`${cfg.module}-delete-${i.ref}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${cfg.module}-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{edit?.id ? `${edit.ref} · Edit` : `New ${cfg.singular}`}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-4">
              {cfg.fields.filter((f) => !f.hide?.(edit)).map((f) => <Field key={f.key} f={f} value={edit[f.key]} onChange={(v) => setEdit((e) => ({ ...e, [f.key]: v }))} team={team} clauses={clauses} />)}
              {cfg.extra && <div className="sm:col-span-2">{cfg.extra(edit, setEdit, load)}</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid={`${cfg.module}-save-btn`}>{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {del?.ref}?</AlertDialogTitle><AlertDialogDescription>A copy is retained in the audit archive. This cannot be undone from the app.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-[#D64545]" data-testid={`${cfg.module}-confirm-delete`}>Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
